import type { ComponentProps, RichTextData } from '@enonic/react-components'
import * as React from 'react'
import { Container, ButtonLink, Prose, Icon } from './index'

export interface FooterLink {
  url: string
  label: string
  newTab: boolean
}

export interface FooterColumn {
  heading: string
  links: FooterLink[]
}

export interface FooterCta {
  url: string
  label: string
}

export interface FooterData {
  contactInfo?: RichTextData
  columns: FooterColumn[]
  cta?: FooterCta
  smallPrint?: string
}

export interface FooterProps {
  logoUrl: string
  footer: FooterData
  meta?: ComponentProps['meta']
  common?: ComponentProps['common']
  component?: ComponentProps['component']
}

export const Footer = ({
  logoUrl,
  footer,
  meta,
  common,
  component,
}: FooterProps) => {
  const { contactInfo, columns, cta, smallPrint } = footer

  return (
    <footer className="bg-blue-900">
      <Container>
        {/* Logo + CTA */}
        <div className="col-span-full flex flex-col gap-6 md:flex-row md:items-center md:justify-between py-8 md:py-12">
          <a href="/" aria-label="Fagskolen Diakonova — til forsiden">
            <img
              src={logoUrl}
              alt=""
              className="h-13 md:h-18 w-auto"
            />
          </a>
          {cta && (
            <ButtonLink href={cta.url} label={cta.label} variant="button" />
          )}
        </div>

        {/* Top divider */}
        <hr className="col-span-full border-t border-peach-500" />

        {/* Contact info */}
        {contactInfo && (
          <div className="col-span-full md:col-span-3 pt-6 md:pt-12">
            <Prose
              data={contactInfo}
              meta={meta}
              common={common}
              component={component}
              className="prose-invert prose-a:text-blue-100 prose-a:decoration-blue-500"
            />
          </div>
        )}

        {/* Link columns */}
        {columns.map((col, i) => (
          <nav
            key={i}
            className="col-span-full md:col-span-3 pt-6 md:pt-12"
            aria-label={col.heading}
          >
            <h2 className="text-base md:text-xl font-bold text-white">
              {col.heading}
            </h2>
            <ul className="mt-4 md:mt-6 flex flex-col gap-2 md:gap-3">
              {col.links.map((link, j) => (
                <li key={j}>
                  <a
                    href={link.url}
                    className="text-base md:text-xl text-white underline decoration-blue-500 transition-colors hover:decoration-blue-300 focus-visible:outline-1 focus-visible:outline-slate-300"
                    {...(link.newTab
                      ? { target: '_blank', rel: 'noopener noreferrer' }
                      : {})}
                  >
                    {link.label}
                    {link.newTab && (
                      <Icon name="north_east" size={16} className="ml-1 align-middle" />
                    )}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        ))}

        {/* Bottom divider */}
        <hr className="col-span-full border-t border-peach-500 mt-6 md:mt-12" />

        {/* Small print */}
        {smallPrint && (
          <p className="col-span-full text-base text-slate-300 py-5 pb-8 md:pb-12">
            {smallPrint}
          </p>
        )}
      </Container>
    </footer>
  )
}
